import { Control, Controller } from "react-hook-form";
import React from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { FormData } from "./PayrollBreakdownEditor";
import { BodyMediumText, TitleMediumText } from "../../FormattedText";
import Gap from "../../../ui_atoms/Gap";
import { useTheme } from "../../../hooks/use-theme";
import { DynamicFee, SalaryDeductionType } from "../../../__generated__/graphql";
import { ChainTextInputProps } from "../../../hooks/use-chain-input-focus";
import QuantityField from "../../QuantityField";
import { DynamicFees } from "./DynamicFees";
import MarkdownView from "../../MarkdownView";
import FullWidthLine from "../../FullWidthLine";

type PayrollBreakdownCommonProps = {
    needWorkDays: boolean;
    control: Control<FormData>;
    chainInputFocus?: (fieldName: string) => ChainTextInputProps;
    maxWorkDays?: number;
    minWorkDays?: number;
    clientDeductions: SalaryDeductionType[];
    dynamicFees: DynamicFee[];
    payrollBreakdownType: "monthly" | "hourly";
};

const PayrollBreakdownCommon: React.FC<PayrollBreakdownCommonProps> = ({
    needWorkDays,
    control,
    chainInputFocus = () => ({}),
    maxWorkDays,
    minWorkDays,
    clientDeductions,
    dynamicFees,
    payrollBreakdownType,
}) => {
    const i18n = useI18n();
    const { colors, fonts } = useTheme();

    if (!needWorkDays && dynamicFees.length === 0) return null;
    return (
        <>
            <FullWidthLine color={colors.accentLighter} />
            <Gap gap={1} />
            <TitleMediumText>{i18n.t("payroll_breakdown.dynamic_fees")}</TitleMediumText>
            {needWorkDays && (
                <>
                    <Gap gap={1 / 3} />
                    <BodyMediumText>
                        {i18n.t("payroll_breakdown.field_total_work_days_title")}
                    </BodyMediumText>
                    <MarkdownView
                        initialNumberOfLines={1}
                        markdownStyle={{
                            paragraph: {
                                ...fonts.bodySmall,
                                color: colors.greyAccessible,
                            },
                        }}
                        markdown={i18n.t(
                            `payroll_breakdown.${payrollBreakdownType}.worked_days.description`,
                        )}
                    />
                    <Gap gap={2 / 3} />
                    <Controller
                        name="totalWorkedDays"
                        control={control}
                        rules={{
                            validate: {
                                exists: (value) => {
                                    if (value === undefined || value === null) {
                                        return i18n.t("payroll_breakdown.error_not_set");
                                    }
                                    return true;
                                },
                                isLessThanMax: (value) => {
                                    if (maxWorkDays && value && value > maxWorkDays) {
                                        return i18n.t("payroll_breakdown.error.total_work_days", {
                                            maxWorkDays,
                                        });
                                    }
                                    return true;
                                },
                                isAtLeast: (value) => {
                                    if (
                                        value != null &&
                                        minWorkDays != null &&
                                        value < minWorkDays
                                    ) {
                                        return i18n.t(
                                            "payroll_breakdown.error.min_total_work_days",
                                            {
                                                minWorkDays,
                                            },
                                        );
                                    }
                                    return true;
                                },
                            },
                        }}
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <QuantityField
                                {...chainInputFocus(name)}
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                            />
                        )}
                    />
                </>
            )}
            {dynamicFees.length > 0 && (
                <DynamicFees
                    control={control}
                    dynamicFees={dynamicFees}
                    clientDeductions={clientDeductions}
                    chainInputFocus={chainInputFocus}
                />
            )}
        </>
    );
};

export default PayrollBreakdownCommon;
